import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { PhaseType } from '@schema/phase'
import {
  creationGuideDisplay,
  InitiativeCreationStep,
} from '../../shared/workflow'
import { ReactNode } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import type { Style } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'

export const CreationGuide = ({
  selectedPhaseType,
  currentStep,
  children,
}: {
  selectedPhaseType: PhaseType | null
  currentStep: InitiativeCreationStep
  children: ReactNode
}) => {
  const steps = creationGuideDisplay(selectedPhaseType, currentStep)
  const isMobile = useMediaQuery()

  return (
    <Column
      alignItems="center"
      style={{
        justifyContent: 'center',
        paddingInline: '5rem',
        '@media (max-width: 1400px)': {
          paddingInline: 0,
        },
        position: 'relative',
      }}
    >
      {isMobile ? (
        <>
          <CreationGuideAssignment steps={steps} activeStep={currentStep} />
          {children}
        </>
      ) : (
        <>
          <CreationGuideAssignment steps={steps} activeStep={currentStep} />
          <Row
            gap="5rem"
            alignItems="flex-start"
            style={{
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Column
              aria-hidden
              style={{
                flex: '1 0 30rem',
                maxWidth: '30rem',
                minHeight: '1px',
              }}
            >
              <span />
            </Column>
            <Column
              alignItems="stretch"
              style={{
                flex: '1 0 40rem',
                maxWidth: '40rem',
                alignSelf: 'stretch',
              }}
            >
              {children}
            </Column>
          </Row>
        </>
      )}
    </Column>
  )
}

const CreationGuideAssignment = ({
  steps,
  activeStep,
}: {
  steps: InitiativeCreationStep[]
  activeStep: InitiativeCreationStep
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const hasStepNumbers = steps.length > 1
  const active = steps.length === 1 ? steps[0] : activeStep
  const isMobile = useMediaQuery()

  const bodyStyle: Style = {
    width: '100%',
    flex: isMobile ? '1 0 30rem' : undefined,
    maxWidth: '30rem',
    gap: '2rem',
    justifyContent: 'center',
    ...(!isMobile
      ? {
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '30rem',
          maxWidth: 'min(30rem, calc(100vw - 2rem))',
          boxSizing: 'border-box',
          maxHeight: 'min(90vh, calc(100dvh - 2rem))',
          overflowY: 'auto',
          zIndex: 2,
          left: 'max(1rem, calc((100vw - min(77.5rem, 85vw)) / 2 + 5rem))',
          '@media (max-width: 1400px)': {
            left: 'max(1rem, calc((100vw - min(77.5rem, 85vw)) / 2))',
          },
        }
      : {}),
  }

  return (
    <Column style={bodyStyle}>
      {steps.map((step, index) => (
        <span key={index}>
          <Title.Hx
            title={step === active ? 'H2' : 'H4'}
            style={{
              color:
                step === active ? 'var(--brand-secondary)' : 'var(--gray-300)',
            }}
          >
            {(hasStepNumbers ? `${index + 1}. ` : '') +
              translations.creationStepTitles[step]}
          </Title.Hx>
          {translations.creationStepDescriptions[step] && (
            <Text.Medium>
              {translations.creationStepDescriptions[step]}
            </Text.Medium>
          )}
        </span>
      ))}
    </Column>
  )
}
